import { makeQuery } from "src/queries/makeQuery";
import type {
  BasicTask,
  MappedTaskParamResource,
  MinimalTaskRun,
  MinimalTaskRunWithRetry,
  RunParamsResponse,
  Task,
  TaskDetails,
  TaskParamResource,
  TaskParamRunsResponse,
  TaskRelatedCompute,
  TaskResource,
  TaskRunEventResponse,
  TaskRunParamValue,
  TaskRunWithParamsData,
  TasksItem,
  TaskWithRetry,
} from "./tasks.types";
import { addRetryProperty, addRetryPropertyToRunTasks } from "./tasks.utils";

/**
 * /api/tasks/:task_id
 */
export const useQueryTask = makeQuery<Task>((p) => `/api/tasks/${p.task_id}`, {
  enabled: (p) => p.task_id && +p.task_id > 10,
});

/**
 * /api/envs/:env_name/apps/:app_name/tasks
 */
export const useQueryTasks = makeQuery<TasksItem[]>(
  (p) => `/api/envs/${p.env_name}/apps/${p.app_name}/tasks`,
  { queryParams: ["timeWindow", "is_debug_mode"] }
);

/**
 * /api/envs/:env_name/apps/:app_name/tasks-basic
 * Lightweight endpoint returning only task names for selectors/dropdowns
 */
export const useQueryBasicTasks = makeQuery<BasicTask[]>(
  (p) => `/api/envs/${p.env_name}/apps/${p.app_name}/tasks-basic`,
  { queryParams: ["timeWindow"] }
);

/**
 * /api/envs/:env_name/apps/:app_name/tasks/:task_name/agg_runs
 */
export const useQueryTaskRuns = makeQuery<TasksItem[]>(
  (p) => `/api/envs/${p.env_name}/apps/${p.app_name}/tasks/${p.task_name}/agg_runs`,
  { queryParams: ["timeWindow"] }
);

/**
 * /api/envs/:env_name/apps/:app_name/tasks/:task_name
 */
export const useQueryTaskDetails = makeQuery<TaskDetails>(
  (p) => `/api/envs/${p.env_name}/apps/${p.app_name}/tasks/${p.task_name}`,
  { queryParams: ["timeWindow"] }
);

/**
 * /api/envs/:env_name/apps/:app_name/task-runs/basic
 *
 * Native select adds `retry` property to tasks. Access via `useQueryMinimalTasksRuns.select`
 * for composition with custom selects.
 */
export const useQueryMinimalTasksRuns = makeQuery<
  MinimalTaskRun[],
  MinimalTaskRunWithRetry[]
>((p) => `/api/envs/${p.env_name}/apps/${p.app_name}/task-runs/basic`, {
  queryParams: ["timeWindow"],
  select: addRetryProperty,
});

/**
 * /api/envs/:env_name/apps/:app_name/tasks/:task_name/params
 *
 * Returns all runs data for a certain task, enriched with params data.
 */
export const useQueryTaskRunsWithParamsData = makeQuery<TaskRunWithParamsData[]>(
  (p) => `/api/envs/${p.env_name}/apps/${p.app_name}/tasks/${p.task_name}/params`,
  { queryParams: ["timeWindow"] }
);

/**
 * /api/envs/:env_name/apps/:app_name/tasks/:task_name/params/:param_name
 *
 * Per-run value for one param (baseline fallback matches `/params`), `is_change` vs prior run in window,
 * plus aggregates. API returns runs newest-first; UI often wants chronological — use `select` to reorder.
 */
export const useQueryTaskParamRuns = makeQuery<TaskParamRunsResponse>(
  (p) =>
    `/api/envs/${p.env_name}/apps/${p.app_name}/tasks/${p.task_name}/params/${p.param_name}`,
  {
    queryParams: ["timeWindow"],
    enabled: (p) => !!p.task_name && !!p.param_name,
  }
);

/**
 * /api/envs/:env_name/apps/:app_name/runs/:app_pit/tasks
 *
 * Native select adds `retry` property to tasks. Access via `useQueryRunTasks.select`
 * for composition with custom selects.
 */
export const useQueryRunTasks = makeQuery<Task[], TaskWithRetry[]>(
  (p) => `/api/envs/${p.env_name}/apps/${p.app_name}/runs/${p.app_pit}/tasks`,
  {
    queryParams: ["task_name", "task_id"],
    select: addRetryPropertyToRunTasks,
  }
);

export const useTasksByTaskNameQuery = makeQuery<TaskResource[]>(
  (p) => `/api/envs/${p.env_name}/apps/${p.app_name}/task-runs`,
  { queryParams: ["task_name", "timeWindow"] }
);

/**
 * /api/envs/:env_name/apps/:app_name/tasks/:task_name/computes
 */
export const useQueryTaskRelatedComputes = makeQuery<TaskRelatedCompute[]>(
  (p) => `/api/envs/${p.env_name}/apps/${p.app_name}/tasks/${p.task_name}/computes`,
  { queryParams: ["timeWindow"] }
);

/**
 * /api/tasks/:task_id/events
 */
export const useQueryTaskRunEvents = makeQuery<TaskRunEventResponse[]>(
  (p) => `/api/tasks/${p.task_id}/events`,
  {
    queryParams: [
      "env_name",
      "is_debug_mode",
      { is_cluster: (p) => !!p.cluster_name },
    ],
  }
);

/**
 * /api/tasks/{task_id}/param/{param_name}/changes-over-time
 */
export const useQueryTaskParamChangesOverTime = makeQuery<TaskRunParamValue[]>(
  (p) => `/api/tasks/${p.task_id}/param/${p.param_name as string}/changes-over-time`
);

const useTaskParamsQueryBase = makeQuery<TaskParamResource[]>(
  (p) => `/api/tasks/${p.task_id}/params`,
  { queryParams: ["timeWindow"] }
);

export const useTaskParamsQuery = (
  task_id?: string | number | null,
  task_name?: string | null
) =>
  useTaskParamsQueryBase({
    params: { task_id },
    enabled: !!task_id,
    select: (params: TaskParamResource[]) =>
      Object.entries(params)
        .map(
          ([key, value]) =>
            ({
              task_id,
              key,
              value,
              black_listed: value.black_listed,
              task_name,
            }) as MappedTaskParamResource
        )
        .sort((a, b) => a.key.localeCompare(b.key)),
  });

export const useRunParamsQuery = makeQuery<RunParamsResponse>(
  (p) => `/api/envs/${p.env_name}/apps/${p.app_name}/runs/${p.app_pit}/params`
);

export const useRunTfsCompareQuery = makeQuery<TaskParamResource[]>(
  (p) => `/api/envs/${p.env_name}/apps/${p.app_name}/runs/${p.app_pit}/tfs`,
  { queryParams: ["task_id"] }
);
